import React, { createContext, useContext } from 'react'

import type { Address, CepResponseProps } from '../types'
import { useAddressCheckout } from '../hook/useAddressCheckout'
import { EMPTY_ADDRESS } from '../variables'

interface AddressContextProps {
  postalCode: string | undefined
  setPostalCode: (postaCode: string) => void
  address: Address
  setAddress: React.Dispatch<React.SetStateAction<Address>>
  resetAddressForNewEntry: () => void
  hasAddressInfo: boolean
  shippingMethod: string | null
  setShippingMethod: React.Dispatch<React.SetStateAction<string | null>>
  shippingData: any | null
  setShippingData: React.Dispatch<React.SetStateAction<any | null>>
  hasCompletedSelection: boolean
  setHasCompletedSelection: React.Dispatch<React.SetStateAction<boolean>>
  shouldShowWarningModal: {
    isOpen: boolean
    type: 'normal' | 'express' | null
    pickupEstimate: string | null
  }
  openWarningModal: (
    type: 'normal' | 'express',
    pickupEstimate?: string | null
  ) => void
  closeWarningModal: () => void
  selectedStore: any | null
  setSelectedStore: React.Dispatch<React.SetStateAction<any | null>>
  isOpenConfirmedAddress: boolean
  setIsOpenConfirmedAddress: React.Dispatch<React.SetStateAction<boolean>>
  getCEP: (cep: string) => Promise<CepResponseProps | null>
}

const AddressContext = createContext<AddressContextProps>({
  postalCode: undefined,
  setPostalCode: () => {},
  address: EMPTY_ADDRESS,
  setAddress: () => {},
  resetAddressForNewEntry: () => {},
  hasAddressInfo: false,
  shippingMethod: null,
  setShippingMethod: () => {},
  shippingData: null,
  setShippingData: () => {},
  hasCompletedSelection: false,
  setHasCompletedSelection: () => {},
  shouldShowWarningModal: {
    isOpen: false,
    type: null,
    pickupEstimate: null,
  },
  openWarningModal: () => {},
  closeWarningModal: () => {},
  selectedStore: null,
  setSelectedStore: () => {},
  isOpenConfirmedAddress: false,
  setIsOpenConfirmedAddress: () => {},
  getCEP: async () => null,
})

export const AddressProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    postalCode,
    setPostalCode,
    hasAddressInfo,
    address,
    setAddress,
    resetAddressForNewEntry,
    shippingMethod,
    setShippingMethod,
    shippingData,
    setShippingData,
    hasCompletedSelection,
    setHasCompletedSelection,
    shouldShowWarningModal,
    openWarningModal,
    closeWarningModal,
    selectedStore,
    setSelectedStore,
    isOpenConfirmedAddress,
    setIsOpenConfirmedAddress,
    getCEP,
  } = useAddressCheckout()

  return (
    <AddressContext.Provider
      value={{
        postalCode,
        setPostalCode,
        address,
        setAddress,
        resetAddressForNewEntry,
        hasAddressInfo,
        shippingMethod,
        setShippingMethod,
        shippingData,
        setShippingData,
        hasCompletedSelection,
        setHasCompletedSelection,
        shouldShowWarningModal,
        openWarningModal,
        closeWarningModal,
        selectedStore,
        setSelectedStore,
        isOpenConfirmedAddress,
        setIsOpenConfirmedAddress,
        getCEP,
      }}
    >
      {children}
    </AddressContext.Provider>
  )
}

export const useAddressContext = (): AddressContextProps => {
  const context = useContext(AddressContext)

  if (!context) {
    throw new Error('useAddressContext must be used within an AddressProvider')
  }

  return context
}
